import React, {
  forwardRef,
  HTMLInputTypeAttribute,
  useImperativeHandle,
  useMemo,
  useState,
  useEffect,
} from "react";

import {
  Button,
  CircularProgress,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from "@mui/material";
import { makeStyles } from "tss-react/mui";
import { EditOutlined } from "@mui/icons-material";

const useStyles = makeStyles()((theme) => ({
  button: {
    marginLeft: theme.spacing(2),
    padding: theme.spacing(1, 4),
    flexGrow: 0,
  },
  input: {
    flexGrow: 1,
  },
}));

type FieldStates = "dirty" | "pristine" | "saved";

export const TextWithConfirmation = forwardRef(
  (
    {
      label,
      onSave,
      endIcon,
      type = "text",
      isLoading = false,
      required = false,
      startValue = "",
      buttonText = "Salvar",
    }: TextWithConfirmationProps,
    ref
  ) => {
    const { classes } = useStyles();

    useImperativeHandle(ref, () => ({
      getValue: () => text,
      getFieldState: () => fieldState,
      valueWasSaved: () => setFieldState("saved"),
    }));

    const [text, setText] = useState(startValue);
    const [fieldState, setFieldState] = useState<FieldStates>("pristine");

    useEffect(() => {
      setText(startValue);
      setFieldState("pristine");
    }, [startValue]);

    const isValid = useMemo(() => {
      if (fieldState === "pristine" || fieldState === "saved") {
        return true;
      } else {
        // user has interacted with the input
        switch (type) {
          default:
            // basic text validation
            return required ? text.length > 0 : true;
        }
      }
    }, [text, required, type, fieldState]);
    const displayText = fieldState === "saved" ? "Salvo" : buttonText;

    const inputProps = useMemo(() => {
      switch (endIcon) {
        case "edit":
          return {
            endAdornment: (
              <InputAdornment position="end">
                <EditOutlined />
              </InputAdornment>
            ),
          };
        default:
          return {};
      }
    }, [endIcon]);

    const handleChange = (event: React.ChangeEvent<HTMLInputElement>) => {
      const newValue = event.target.value;
      if (fieldState === "pristine") {
        setFieldState("dirty");
      } else {
        // if the user is deleting changes, don't allow saving
        if (newValue === startValue) {
          setFieldState("pristine");
        }
      }
      setText(newValue);
    };

    const handleSave = () => {
      if (isValid) {
        onSave(text);
      }
    };

    return (
      <Grid
        item
        container
        justifyContent="space-between"
        alignItems="flex-start"
      >
        <TextField
          value={text}
          onChange={handleChange}
          label={label}
          InputProps={inputProps}
          className={classes.input}
          error={!isValid}
          helperText={!isValid ? "Campo obrigatório" : ""}
        />
        <Button
          className={classes.button}
          variant="contained"
          onClick={handleSave}
          disabled={
            isLoading ||
            !isValid ||
            !text ||
            fieldState === "pristine" ||
            fieldState === "saved"
          }
        >
          {isLoading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            <Typography fontWeight="700" variant="caption" color="common.white">
              {displayText}
            </Typography>
          )}
        </Button>
      </Grid>
    );
  }
);

type endIconOptions = "edit";

export type TextWithConfirmationProps = {
  label: string;
  onSave: (value: string) => void;
  endIcon?: endIconOptions;
  isLoading?: boolean;
  startValue?: string;
  buttonText?: string;
  required?: boolean;
  type?: HTMLInputTypeAttribute;
};
